/**
 * ARCA Points-of-Sale Policy (TV1-2210 S6)
 *
 * Fetches the points of sale the fiscal authority has REGISTERED for the
 * invoicing integration currently selected on the billing-entity form
 * (`GET /billing-entity-points-of-sale` -> ARCA `FEParamGetPtosVenta`).
 *
 * This is what turns the point-of-sale chip editor from free text into a
 * picker. A point of sale a user TYPES is not usable: ARCA does not
 * auto-register one when you invoice with it, and production rejects it with
 * `11002` — an error homologación cannot reproduce, so nothing else in the
 * system would ever catch it before a customer's first real invoice.
 *
 * An EMPTY list is a real, reportable answer (ARCA `602` = no registered point
 * of sale), not an error, and the form says so out loud.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { AccountState } from '../state';
import { AccountCommand } from '../commands';

/** The integration the fetch should be for — `null` when there is nothing to fetch. */
const targetIntegrationId = ( state: AccountState ): string | null => {
  if ( state.billing.formMode.type === 'closed' ) return null;
  return state.billing.formData.invoicingIntegrationId || null;
};

export const createArcaPointsOfSalePolicy = (
  backend: ClientLibraryType
): Policy<AccountState, AccountCommand> => ({
  name: 'account-arca-points-of-sale',

  // React when the form's selected integration differs from the one the loaded
  // list belongs to. That covers opening the form on an existing entity, picking
  // an integration, and switching to another — without re-fetching on every
  // unrelated keystroke, because the comparison is against what was FETCHED.
  shouldReact: ( _prev, next ) => {
    const target = targetIntegrationId( next );
    if ( !target ) return false;
    const { arcaPointsOfSale } = next.billing;
    if ( arcaPointsOfSale.loading ) return false;
    return arcaPointsOfSale.integrationId !== target;
  },

  onStateChange: async ({ next, ctx }) => {
    const integrationId = targetIntegrationId( next );
    if ( !integrationId ) return;

    // The integration ROW carries the connector identity the endpoint needs. It
    // is already in this slice (`invoicingIntegrationsSyncPolicy`), so no second
    // hop — and the endpoint derives the TENANT from the authenticated user, not
    // from anything sent here.
    const integration = next.billing.invoicingIntegrations[ integrationId ];
    if ( !integration ) return;

    ctx.dispatch(
      { type: 'account/billing/arcaPointsOfSale/loading', payload: { integrationId }},
      { source: 'api' }
    );

    try {
      const result = await backend.get.billing_entity_points_of_sale({
        connectorId: String( integration.connectorId ),
        integrationUserId: String( integration.integrationUserId ?? '' )
      });
      ctx.dispatch(
        {
          type: 'account/billing/arcaPointsOfSale/loaded',
          payload: { integrationId, items: result?.pointsOfSale ?? []}
        },
        { source: 'api' }
      );
    } catch ( error ) {
      // Does NOT rethrow: a failed lookup must not put a global error toast over
      // a form the user is still filling in. The form renders the failure on the
      // picker itself, where the user can retry by re-selecting the integration.
      ctx.dispatch(
        {
          type: 'account/billing/arcaPointsOfSale/failed',
          payload: {
            integrationId,
            error: 'No se pudo consultar los puntos de venta registrados en ARCA para esta integración.'
          }
        },
        { source: 'api' }
      );
    }
  }
});
